import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle } from 'lucide-react';
type Language = 'es' | 'en' | 'pt';
const translations = {
  es: {
    title: 'Preguntas',
    titleHighlight: 'Frecuentes',
    faqs: [{
      question: '¿Cuáles son las tasas del servidor?',
      answer: 'Nuestro servidor cuenta con una tasa de experiencia dinámica de 1000x, tasa de drop del 50% y drop de zen del 100%. Estas tasas están cuidadosamente balanceadas para ofrecer una progresión rápida pero significativa.'
    }, {
      question: '¿El servidor es pay-to-win?',
      answer: 'Absolutamente no. Todo el equipo, armas y mejoras pueden obtenerse jugando. Las donaciones están estrictamente limitadas a artículos cosméticos y servicios de conveniencia que no afectan el balance del juego.'
    }, {
      question: '¿Cómo funciona el sistema de resets?',
      answer: 'El nivel máximo es 400. Al alcanzarlo, puedes realizar un reset que te otorgará puntos adicionales para distribuir. El límite actual es de 50 resets, manteniendo un entorno competitivo y balanceado.'
    }, {
      question: '¿Hay soporte en español?',
      answer: 'Sí, contamos con un equipo de moderadores y administradores hispanohablantes disponibles 24/7 en nuestro servidor de Discord para ayudarte con cualquier problema o duda.'
    }, {
      question: '¿Cuándo se realizan los eventos?',
      answer: 'Tenemos eventos automáticos diarios (Blood Castle, Devil Square, Chaos Castle) y eventos especiales organizados por el staff todos los fines de semana, incluyendo el épico Castle Siege.'
    }, {
      question: '¿Cómo reporto bugs o jugadores usando hacks?',
      answer: 'Puedes reportar cualquier anomalía directamente a través de nuestro sistema de tickets en Discord. Nuestro equipo revisa los reportes diariamente y tomamos acciones inmediatas contra los tramposos.'
    }]
  },
  en: {
    title: 'Frequently Asked',
    titleHighlight: 'Questions',
    faqs: [{
      question: 'What are the server rates?',
      answer: 'Our server features a dynamic 1000x experience rate, 50% drop rate, and 100% zen drop. These rates are carefully balanced to offer fast yet meaningful progression.'
    }, {
      question: 'Is the server pay-to-win?',
      answer: 'Absolutely not. All gear, weapons, and upgrades can be obtained through gameplay. Donations are strictly limited to cosmetic items and convenience services that do not affect game balance.'
    }, {
      question: 'How does the reset system work?',
      answer: 'The maximum level is 400. Upon reaching it, you can perform a reset which grants additional points to distribute. The current limit is 50 resets, maintaining a competitive and balanced environment.'
    }, {
      question: 'Is there English support?',
      answer: 'Yes, we have a team of English-speaking moderators and administrators available 24/7 on our Discord server to help you with any issues or questions.'
    }, {
      question: 'When are events held?',
      answer: 'We have daily automated events (Blood Castle, Devil Square, Chaos Castle) and special staff-organized events every weekend, including the epic Castle Siege.'
    }, {
      question: 'How do I report bugs or hackers?',
      answer: 'You can report any anomalies directly through our ticket system on Discord. Our team reviews reports daily and we take immediate action against cheaters.'
    }]
  },
  pt: {
    title: 'Perguntas',
    titleHighlight: 'Frequentes',
    faqs: [{
      question: 'Quais são as taxas do servidor?',
      answer: 'Nosso servidor possui uma taxa de experiência dinâmica de 1000x, taxa de drop de 50% e drop de zen de 100%. Essas taxas são cuidadosamente balanceadas para oferecer uma progressão rápida, mas significativa.'
    }, {
      question: 'O servidor é pay-to-win?',
      answer: 'Absolutamente não. Todo o equipamento, armas e melhorias podem ser obtidos jogando. As doações são estritamente limitadas a itens cosméticos e serviços de conveniência que não afetam o equilíbrio do jogo.'
    }, {
      question: 'Como funciona o sistema de resets?',
      answer: 'O nível máximo é 400. Ao alcançá-lo, você pode realizar um reset que concederá pontos adicionais para distribuir. O limite atual é de 50 resets, mantendo um ambiente competitivo e equilibrado.'
    }, {
      question: 'Há suporte em português?',
      answer: 'Sim, temos uma equipe de moderadores e administradores que falam português disponíveis 24/7 em nosso servidor do Discord para ajudá-lo com qualquer problema ou dúvida.'
    }, {
      question: 'Quando os eventos são realizados?',
      answer: 'Temos eventos automáticos diários (Blood Castle, Devil Square, Chaos Castle) e eventos especiais organizados pela equipe todos os fins de semana, incluindo o épico Castle Siege.'
    }, {
      question: 'Como reporto bugs ou hackers?',
      answer: 'Você pode relatar qualquer anomalia diretamente através do nosso sistema de tickets no Discord. Nossa equipe analisa os relatórios diariamente e tomamos medidas imediatas contra trapaceiros.'
    }]
  }
};
export function FAQ() {
  const [language, setLanguage] = useState<Language>('es');
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const t = translations[language];
  useEffect(() => {
    const savedLang = localStorage.getItem('language') as Language;
    if (savedLang) setLanguage(savedLang);
    const handleLanguageChange = (e: CustomEvent<Language>) => {
      setLanguage(e.detail);
    };
    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => window.removeEventListener('languageChange', handleLanguageChange as EventListener);
  }, []);
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section className="py-16 md:py-24 relative z-10 bg-darkerBlue" data-id="element-252">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-253">
        {/* Header */}
        <div className="text-center mb-16" data-id="element-254">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 flex items-center justify-center gap-4" data-id="element-255">
            <HelpCircle className="w-10 h-10 text-gold" data-id="element-256" />
            {t.title}{' '}
            <span className="text-gradient-gold" data-id="element-257">{t.titleHighlight}</span>
          </motion.h2>
          <motion.div initial={{
          opacity: 0,
          scale: 0
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="w-24 h-1 bg-gold mx-auto rounded-full" data-id="element-258" />
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-4" data-id="element-259">
          {t.faqs.map((faq, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className={`glass-panel rounded-xl border transition-colors duration-300 overflow-hidden ${openIndex === index ? 'border-gold/50 bg-white/5' : 'border-white/10 hover:border-white/20'}`} data-id="element-260">
              <button onClick={() => toggleFAQ(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-261">
                <span className={`font-bold font-cinzel text-lg transition-colors ${openIndex === index ? 'text-gold' : 'text-white'}`} data-id="element-262">
                  {faq.question}
                </span>
                <ChevronDown className={`w-5 h-5 transition-transform duration-300 flex-shrink-0 ${openIndex === index ? 'text-gold rotate-180' : 'text-gray-400'}`} data-id="element-263" />
              </button>

              <AnimatePresence data-id="element-264">
                {openIndex === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} data-id="element-265">
                    <div className="px-6 pb-5 text-gray-300 leading-relaxed border-t border-white/5 pt-4" data-id="element-266">
                      {faq.answer}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>
      </div>
    </section>;
}